import { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { createFilter, components as ReactSelectComponents } from 'react-select';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
import PropTypes from 'prop-types';
import Icon from '../renders/icon/Icon';
import ICONS from '../../enums/icons';
import './selectCreatableInput.scss';

// These must be defined OUTSIDE the component. If defined inside, react-select receives
// new references on every render and remounts the option elements, breaking mouse clicks
// (mousedown fires on old instance, mouseup fires on new instance → click is lost).
const DropdownIndicator = (props) => (
    <ReactSelectComponents.DropdownIndicator {...props}>
        <Icon icon={ICONS.CHEVRON_DOWN} className='h-4 w-4 text-gray-500' />
    </ReactSelectComponents.DropdownIndicator>
);

const Option = (props) => {
    if (props.data && props.data.__isNew__) {
        return null;
    }
    return <ReactSelectComponents.Option {...props} />;
};

const IndicatorSeparator = () => null;

/**
 *
 * @param root0 props
 * @param root0.options array
 * @param root0.value any
 * @param root0.onChange function
 * @param root0.placeholder oneOfType([object, string])
 * @param root0.className string
 * @param root0.autoFocus bool
 * @param root0.namespace string
 * @param root0.inputPattern instanceOf(RegExp)
 * @returns React.Component
 */
function SelectCreatableInput({ options, value, onChange, placeholder, className, autoFocus, namespace, inputPattern }) {

    // State
    const [optionsTranslated, setOptionsTranslated] = useState([]);
    const [inputValue, setInputValue] = useState('');

    // Hooks
    useEffect(() => {
        setOptionsTranslated(
            options.map(option => {
                const label = option.namespace
                    ? getTranslatedText(option.name, option.namespace)
                    : option.name || option.value;
                return {
                    label: label,
                    value: option.value
                };
            })
        );
    }, [options, namespace]);

    const onSelect = (selectedOption) => {
        onChange(selectedOption?.value ?? '');
        setInputValue('');
    };

    const isValidNewOption = (inputVal) => {
        return !inputPattern || inputPattern.test(inputVal);
    };

    // Update form value in real-time as the user types.
    // Guard on action='input-change' to avoid calling onChange('') when react-select
    // fires onInputChange internally on blur/menu-close.
    const onInputChange = (newValue, { action } = {}) => {
        if (action !== 'input-change') { return inputValue; }
        if (inputPattern) {
            const charClass = inputPattern.source.match(/\[([^\]]+)\]/)?.[1] || '';
            const filtered = newValue.replace(new RegExp(`[^${charClass}]`, 'g'), '');
            setInputValue(filtered);
            onChange(filtered);
            return filtered;
        }
        setInputValue(newValue);
        onChange(newValue);
        return newValue;
    };

    // Fallback: commit on blur or Enter in case the user stops without interacting further.
    const handleAutoCreate = () => {
        if (inputValue && isValidNewOption(inputValue)) {
            onChange(inputValue);
            setInputValue('');
        }
    };

    // While typing (inputValue non-empty), pass null as value so react-select does not
    // interpret the real-time onChange update as a "selection" and close the menu.
    const selectedValue = inputValue
        ? null
        : (optionsTranslated.find(option => option.value === value) ?? (value ? { label: value, value } : null));

    return (
        <CreatableSelect
            className={className}
            classNamePrefix='select-creatable-input'
            options={optionsTranslated}
            value={selectedValue}
            onChange={onSelect}
            placeholder={placeholder}
            filterOption={createFilter({
                ignoreCase: true,
                ignoreAccents: true,
                matchFrom: 'any',
                stringify: option => `${option.label}`
            })}
            autoFocus={autoFocus}
            onInputChange={onInputChange}
            inputValue={inputValue}
            isValidNewOption={isValidNewOption}
            onBlur={handleAutoCreate}
            onKeyDown={(e) => e.key === 'Enter' && handleAutoCreate()}
            components={{
                DropdownIndicator,
                Option,
                IndicatorSeparator
            }}
            formatCreateLabel={() => ''}
        />
    );
}

SelectCreatableInput.propTypes = {
    options: PropTypes.array.isRequired,
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    className: PropTypes.string,
    autoFocus: PropTypes.bool,
    namespace: PropTypes.string,
    inputPattern: PropTypes.instanceOf(RegExp)
};

export default SelectCreatableInput;
